import { useState } from 'react';
import { FilterCatalog } from '../../core/model/filterCatalog';
import type { SearchCriteria } from '../../core/model/search';
import { Formatters } from '../../core/util/formatters';
import { MAX_ADVANCE_DAYS, MAX_PARTY_ONLINE } from '../../core/validation/bookingValidator';
import { plusDays, today } from '../../core/util/datetime';
import { S } from '../../i18n/strings';
import { useCriteria } from '../../state/CriteriaContext';
import { Dialog } from './Dialog';
import { CalendarIcon, ClockIcon, PinIcon, UsersIcon } from './icons';

/**
 * Barra de los cuatro ejes de la búsqueda: ubicación, fecha, hora y personas.
 *
 * Siempre muestra un valor en cada eje. Dejar alguno vacío llevaría a una consulta que el
 * servidor no puede responder y a una pantalla en un estado indefinido.
 */
export function SearchCriteriaBar() {
  const { criteria } = useCriteria();
  const [open, setOpen] = useState<null | 'location' | 'date' | 'time' | 'party'>(null);

  return (
    <>
      <div className="criteria-bar">
        <CriteriaButton
          icon={<PinIcon size={16} />}
          label={S.pickerLocation}
          value={criteria.location.label}
          onClick={() => setOpen('location')}
        />
        <CriteriaButton
          icon={<CalendarIcon size={16} />}
          label={S.pickerDate}
          value={Formatters.friendlyDate(criteria.date, today())}
          onClick={() => setOpen('date')}
        />
        <CriteriaButton
          icon={<ClockIcon size={16} />}
          label={S.pickerTime}
          value={Formatters.time(criteria.time)}
          onClick={() => setOpen('time')}
        />
        <CriteriaButton
          icon={<UsersIcon size={16} />}
          label={S.pickerParty}
          value={Formatters.partySize(criteria.partySize)}
          onClick={() => setOpen('party')}
        />
      </div>

      {open === 'location' && <LocationPicker onClose={() => setOpen(null)} />}
      {open === 'date' && <DatePicker onClose={() => setOpen(null)} />}
      {open === 'time' && <TimePicker onClose={() => setOpen(null)} />}
      {open === 'party' && <PartyPicker onClose={() => setOpen(null)} />}
    </>
  );
}

function CriteriaButton({
  icon,
  label,
  value,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="criteria-bar__button" onClick={onClick}>
      <span className="criteria-bar__label">{label}</span>
      <span className="criteria-bar__value">
        {icon} {value}
      </span>
    </button>
  );
}

function LocationPicker({ onClose }: { onClose: () => void }) {
  const { criteria, setLocation } = useCriteria();
  const currentId = criteria.location.kind === 'NAMED' ? criteria.location.id : null;

  return (
    <Dialog title={S.pickerLocation} onClose={onClose}>
      <div className="stack stack--tight">
        {FilterCatalog.locations.map((location) => (
          <button
            key={location.id}
            type="button"
            className="chip"
            aria-pressed={currentId === location.id}
            style={{ justifyContent: 'flex-start', minHeight: 44 }}
            onClick={() => {
              setLocation({ kind: 'NAMED', id: location.id, label: location.label });
              onClose();
            }}
          >
            <PinIcon size={16} /> {location.label}
          </button>
        ))}
      </div>
      {/* "Cerca de mí" solo se ofrece si el navegador puede geolocalizar: un botón que no puede
          cumplir su promesa es peor que su ausencia. */}
      {typeof navigator !== 'undefined' && 'geolocation' in navigator && (
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                setLocation({
                  kind: 'COORDINATES',
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  label: 'Cerca de mí',
                });
                onClose();
              },
              // Permiso denegado: se conserva la zona elegida a mano, que siempre funciona.
              () => onClose(),
            );
          }}
        >
          Usar mi ubicación
        </button>
      )}
    </Dialog>
  );
}

function DatePicker({ onClose }: { onClose: () => void }) {
  const { criteria, setDate } = useCriteria();
  const start = today();
  // Solo se ofrecen fechas reservables: el límite de 90 días es la misma regla que valida el
  // formulario, así que el usuario no puede elegir algo que después se rechace.
  const days = Array.from({ length: 30 }, (_, i) => plusDays(start, i));

  return (
    <Dialog title={S.pickerDate} onClose={onClose}>
      <div className="stack stack--tight">
        <input
          className="field__control"
          type="date"
          value={criteria.date}
          min={start}
          max={plusDays(start, MAX_ADVANCE_DAYS)}
          onChange={(event) => {
            if (event.target.value) {
              setDate(event.target.value);
              onClose();
            }
          }}
        />
        <div className="chip-row">
          {days.map((day) => (
            <button
              key={day}
              type="button"
              className="chip"
              aria-pressed={criteria.date === day}
              onClick={() => {
                setDate(day);
                onClose();
              }}
            >
              {Formatters.friendlyDate(day, start)}
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function TimePicker({ onClose }: { onClose: () => void }) {
  const { criteria, setTime } = useCriteria();
  const times = Array.from({ length: 32 }, (_, i) => {
    const minutes = 7 * 60 + i * 30;
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  });

  return (
    <Dialog title={S.pickerTime} onClose={onClose}>
      <div className="row" style={{ gap: 8 }}>
        {times.map((time) => (
          <button
            key={time}
            type="button"
            className="chip"
            aria-pressed={criteria.time === time}
            onClick={() => {
              setTime(time);
              onClose();
            }}
          >
            {time}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

function PartyPicker({ onClose }: { onClose: () => void }) {
  const { criteria, setPartySize } = useCriteria();

  return (
    <Dialog title={S.pickerParty} onClose={onClose}>
      <div className="row" style={{ gap: 8 }}>
        {Array.from({ length: MAX_PARTY_ONLINE }, (_, i) => i + 1).map((size) => (
          <button
            key={size}
            type="button"
            className="chip"
            aria-pressed={criteria.partySize === size}
            onClick={() => {
              setPartySize(size);
              onClose();
            }}
          >
            {Formatters.partySize(size)}
          </button>
        ))}
      </div>
      {/* Por encima del máximo en línea no se bloquea sin más: se dice qué hacer. */}
      <p className="muted">{S.validationPartyMax}</p>
    </Dialog>
  );
}

export function criteriaSummary(criteria: SearchCriteria): string {
  return [
    Formatters.friendlyDate(criteria.date, today()),
    Formatters.time(criteria.time),
    Formatters.partySize(criteria.partySize),
  ].join(' · ');
}
